/**
 * RecordCategoryFormModal — Ustawienia kartoteki systemowej → zakładka
 * „Kategorie" (`RecordCategoriesTab`). Modal add/edit dla wiersza
 * `record_categories` — WYŁĄCZNIE nazwa + kolejność (bez koloru/ikony,
 * świadomy kontrast do `CategoryFormModal` modułów systemowych).
 *
 * Walidacja: name 1-100 znaków (mirror `POST_BODY_SCHEMA`/`PATCH_BODY_SCHEMA`
 * w `apps/api/app/api/record-categories/route.ts`), sortOrder liczba całkowita
 * >= 0 (opcjonalna — puste pole = 0).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';

export interface RecordCategoryFormValues {
  name: string;
  sortOrder: number;
}

export interface RecordCategoryFormModalProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues: RecordCategoryFormValues;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (values: RecordCategoryFormValues) => void;
}

const INTEGER_REGEX = /^\d+$/;

export function RecordCategoryFormModal({
  open,
  mode,
  initialValues,
  saving,
  onClose,
  onSubmit,
}: RecordCategoryFormModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialValues.name);
  const [sortOrderStr, setSortOrderStr] = useState(String(initialValues.sortOrder));
  const [errors, setErrors] = useState<{ name?: string; sortOrder?: string }>({});

  // Reset stanu na każde otwarcie modala — initialValues mogą się zmienić między
  // edycją kategorii A → zamknij → edycją kategorii B.
  useEffect(() => {
    if (open) {
      setName(initialValues.name);
      setSortOrderStr(String(initialValues.sortOrder));
      setErrors({});
    }
  }, [open, initialValues.name, initialValues.sortOrder]);

  function validate(): boolean {
    const next: { name?: string; sortOrder?: string } = {};
    const trimmed = name.trim();
    if (!trimmed) next.name = t('tenant.recordCategories.modal.errors.nameRequired');
    else if (trimmed.length > 100) next.name = t('tenant.recordCategories.modal.errors.nameLength');
    const trimmedSortOrder = sortOrderStr.trim();
    if (trimmedSortOrder && !INTEGER_REGEX.test(trimmedSortOrder)) {
      next.sortOrder = t('tenant.recordCategories.modal.errors.sortOrderInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      name: name.trim(),
      sortOrder: sortOrderStr.trim() ? parseInt(sortOrderStr, 10) : 0,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'add'
          ? t('tenant.recordCategories.modal.addTitle')
          : t('tenant.recordCategories.modal.editTitle')
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="record-category-form"
            variant="primary"
            loading={saving}
            data-testid="record-category-form-save"
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      {/* noValidate — własny `validate()` (ostylowany, po polsku, §4.12/§4.5) jest jedynym
          źródłem walidacji; bez tego natywne `required` ucięłoby submit przed handlerem. */}
      <form id="record-category-form" onSubmit={handleSubmit} className="space-y-3" noValidate>
        <Input
          label={t('tenant.recordCategories.modal.nameLabel')}
          error={errors.name}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus
          data-testid="record-category-form-name"
        />
        <Input
          label={t('tenant.recordCategories.modal.sortOrderLabel')}
          hint={t('tenant.recordCategories.modal.sortOrderHint')}
          error={errors.sortOrder}
          type="number"
          min={0}
          step={1}
          value={sortOrderStr}
          onChange={(e) => setSortOrderStr(e.target.value)}
          data-testid="record-category-form-sort-order"
        />
      </form>
    </Modal>
  );
}
